import { type AdSet, EMPTY_EXCLUDED_GEO, type MetaAdDraft, type MetaAdDraftInput, type TargetingConfig } from '../../types/metaAdDraft.js';
import { LEGACY_AD_SET_ID, migrateDraftShape } from '../../lib/metaAdMigration.js';

// A complete, empty MetaAdDraftInput with sensible defaults — passes the lenient
// draft schema (accountId picked later). Defaults: objective Leads (Snap's primary
// use), budgetMode CBO (the mode used most of the time — a single campaign budget
// Meta distributes across ad sets), publishStatus ACTIVE (live on publish; the AM
// can flip to Publish Off), automatic placements, single-image / instant-form, ONE
// empty ad set. Defaults apply ONLY to fresh drafts — the read-time migration never
// overrides an existing/migrated draft's saved budgetMode or publishStatus.

const EMPTY_TARGETING: TargetingConfig = {
  geoLevel: 'country',
  countries: [],
  cities: [],
  regions: [],
  places: [],
  neighborhoods: [],
  zips: [],
  customLocations: [],
  excluded: EMPTY_EXCLUDED_GEO,
  locationTypes: 'home_recent',
  ageMin: 18,
  ageMax: 65,
  gender: 'all',
  interests: [],
  sourceType: 'manual',
  sourcePresetId: null,
  sourceCampaignId: null,
  sourceCampaignName: null,
  sourceAdSetId: null,
  sourceAdSetName: null,
  rawPayload: null,
};

// A fresh, empty TargetingConfig — the seed for a STANDALONE audience builder
// (Meta Setup hub "Create audience", no ad context). Same default audience the ad
// builder starts an ad set with, so a hub-built audience round-trips with apply.
export function emptyTargeting(): TargetingConfig {
  return { ...EMPTY_TARGETING };
}

// A fresh, empty ad set. `id` defaults to the legacy/single constant; UI-created
// additional ad sets get a fresh uuid (see AdSetsSection.addAdSet).
export function emptyAdSet(id: string = LEGACY_AD_SET_ID): AdSet {
  return {
    id,
    name: '',
    targeting: { ...EMPTY_TARGETING },
    budget: null,
    datesOverridden: false,
    startDate: null,
    endDate: null,
    status: 'ACTIVE', // per-ad-set Active/Off, default ACTIVE
    publishState: null,
  };
}

export function emptyMetaAdDraftInput(): MetaAdDraftInput {
  return {
    title: 'Untitled draft',
    description: null,
    accountId: '',
    campaign: {
      objective: 'LEADS',
      budgetMode: 'CBO',
      budget: null,
      publishStatus: 'ACTIVE',
      optimizationGoal: 'LEAD_GENERATION',
      customEventType: null,
      destinationType: 'instant_form',
      facebookPageId: null,
      facebookPageName: null,
      placements: { mode: 'automatic', manualConfig: null },
      defaultStartDate: null,
      defaultEndDate: null,
      scheduleUseAustralianEastern: false, // default: resolve schedule in the account tz
    },
    adSets: [emptyAdSet()],
    ads: {
      name: '',
      instagramAccountId: null,
      instagramAccountName: null,
      format: 'single_image',
      destination: { type: 'instant_form', leadGenFormId: '', leadGenFormName: '' },
      creative: {
        useDynamicCreative: false, // Phase 1 — standard single-text ad by default
        headlines: [''],
        primaryTexts: [''],
        descriptions: [''],
        callToAction: 'SIGN_UP',
        assetType: 'image',
        feedAsset: { type: null, url: null, path: null, thumbnailUrl: null, thumbnailPath: null },
        storyAsset: { type: null, url: null, path: null, thumbnailUrl: null, thumbnailPath: null },
      },
      pixelId: null,
      utmParams: { source: 'facebook', medium: 'paid_social', campaign: '', content: '', term: '' },
      status: 'ACTIVE', // per-ad Active/Off (primary ad), default ACTIVE
      additionalAds: [], // Phase 2 — single-ad draft by default
    },
    // Build Campaign Phase 2: set only when a draft is assembled from a rollout.
    driveSource: null,
    attachTarget: null,
    clonedFrom: null,
  };
}

// Extract the editable MetaAdDraftInput subset from a fetched draft. migrateDraftShape
// is the ONE multi-ad-set migration (legacy single-ad-set → adSets[]/ads + the
// campaign-wide-locked config), folding in the targeting/creative/objective/placement
// shape migrations. Defensive: the server already normalises on read, but re-run here
// so a stale cache can't surface an old shape.
export function draftToInput(draft: MetaAdDraft): MetaAdDraftInput {
  const { campaign, adSets, ads } = migrateDraftShape(draft);
  return {
    title: draft.title,
    description: draft.description,
    accountId: draft.accountId,
    campaign,
    adSets,
    ads,
    // Build Campaign Phase 2: round-trip the Drive snapshot through edits/autosave.
    driveSource: draft.driveSource ?? null,
    // "Add an ad to a live ad set": round-tripped, never edited client-side.
    attachTarget: draft.attachTarget ?? null,
    clonedFrom: draft.clonedFrom ?? null,
  };
}
